/**
 * 日付関連のユーティリティ関数
 */

import logger from './logger';

/**
 * 日付を日本語フォーマットで表示
 */
export function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Tokyo',
    });
  } catch {
    logger.warn('Invalid date string:', dateString);
    return dateString;
  }
}

/**
 * 日付のみをフォーマット
 */
export function formatDateOnly(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    logger.warn('Invalid date string:', dateString);
    return dateString;
  }
}

/**
 * 時刻のみをフォーマット
 */
export function formatTimeOnly(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Tokyo',
    });
  } catch {
    logger.warn('Invalid date string:', dateString);
    return dateString;
  }
}

/**
 * 相対時間を表示（「○分前」など）
 */
export function formatRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) {
      return 'たった今';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}分前`;
    } else if (diffHours < 24) {
      return `${diffHours}時間前`;
    } else if (diffDays < 7) {
      return `${diffDays}日前`;
    } else {
      return formatDateOnly(dateString);
    }
  } catch {
    logger.warn('Invalid date string:', dateString);
    return dateString;
  }
}

/**
 * 日本語の曜日配列
 */
const JAPANESE_WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'] as const;

/**
 * 日付文字列を日本語表示形式にフォーマット（スケジュール表示用）
 * @param dateString YYYY-MM-DD形式の日付文字列
 * @returns "M月D日 (曜)" 形式の文字列
 * @example
 * formatDateForDisplay('2024-01-15') // "1月15日 (月)"
 */
export function formatDateForDisplay(dateString: string): string {
  try {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekday = JAPANESE_WEEKDAYS[date.getDay()];
    return `${month}月${day}日 (${weekday})`;
  } catch {
    logger.warn('Invalid date string:', dateString);
    return dateString;
  }
}

/**
 * 今日の日付をYYYY-MM-DD形式で取得
 * @returns YYYY-MM-DD形式の今日の日付文字列
 */
export function getTodayString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

/**
 * 日付文字列が今日かどうかを判定
 * @param dateString YYYY-MM-DD形式の日付文字列
 * @returns 今日の場合true
 */
export function isToday(dateString: string): boolean {
  return dateString === getTodayString();
}

/**
 * 時刻文字列のフォーマット検証
 * @param timeString HH:MM形式の時刻文字列
 * @returns 有効な時刻形式の場合true
 */
export function isValidTimeFormat(timeString: string): boolean {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(timeString);
}

/**
 * 時刻文字列を比較（同日内での前後比較）
 * @param time1 HH:MM形式の時刻文字列
 * @param time2 HH:MM形式の時刻文字列
 * @returns time1がtime2より前の場合-1、後の場合1、同じ場合0
 */
export function compareTime(time1: string, time2: string): number {
  return time1.localeCompare(time2);
}
